import { useMemo } from 'react';
import { Anchor, Button, Checkbox, Group, Input, Paper, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { handlingUnitsStepMessages } from './handling-units-step-messages';
import { WizardCommodityCard } from './WizardCommodityCard';
import { PACKAGING_TYPE_OPTIONS } from '../../../../ltl/constants';
import { CustomNumberInput, DimensionsInput } from '../../../common';
import { Dropdown } from '../../../common/Dropdown';
import { freightFormMessages } from '../../../freight-browser/components/freight-form/freight-form-messages';
import { CalculateDensityButton } from '../../../freight-density/CalculateDensityButton';
import { createEmptyCommodity } from '../../create-empty-handling-unit';
import { sliceFieldErrors } from '../../wizard-step-validation';

import type { DimensionUnit, Dimensions } from '../../../../types/dimensions';
import type { FreightPackagingType, LtlCommodity, LtlHandlingUnit } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';
import type { FreightDensityAction } from '../../../freight-density/RecommendedFreightClassModal';

export type WizardHandlingUnitCardProps = {
  unit: LtlHandlingUnit;
  index: number;
  displayWeightUnit: WeightUnit;
  displayDimensionUnit: DimensionUnit;
  sellerFreightProviderId?: number;
  onChange: (next: LtlHandlingUnit) => void;
  onRemove?: () => void;
  isHazmatEnabled?: boolean;
  onDensityAction?: (action: FreightDensityAction) => void;
  onCommodityPresetSelect?: (presetId: number) => void;
  /**
   * Formatted inline error messages keyed by field path, already sliced to this unit's prefix
   * (e.g. `'length'`, `'commodities.0.weight'`).
   */
  fieldErrors?: Record<string, string>;
};

export const WizardHandlingUnitCard = ({
  unit,
  index,
  displayWeightUnit,
  displayDimensionUnit,
  sellerFreightProviderId,
  onChange,
  onRemove,
  isHazmatEnabled,
  onDensityAction,
  onCommodityPresetSelect,
  fieldErrors,
}: WizardHandlingUnitCardProps) => {
  const intl = useIntl();

  const packagingTypeData = useMemo(
    () => PACKAGING_TYPE_OPTIONS.map((option) => ({ value: option.value, label: intl.formatMessage(option.label) })),
    [intl],
  );

  const dimensionsValue: Dimensions = {
    length: unit.length,
    width: unit.width,
    height: unit.height,
    unit: (unit.dimensionUnit as Dimensions['unit']) ?? displayDimensionUnit,
  };

  const handleQuantityChange = (value: number | string) => {
    const parsed = typeof value === 'string' ? parseInt(value, 10) || 1 : value;
    onChange({ ...unit, quantity: parsed });
  };

  const handleTypeChange = (value: string | null) => {
    if (value) onChange({ ...unit, packagingType: value as FreightPackagingType });
  };

  const handleStackableChange = (isStackable: boolean) => onChange({ ...unit, isStackable });

  const handleDimensionsChange = (next: Dimensions) =>
    onChange({ ...unit, length: next.length, width: next.width, height: next.height, dimensionUnit: next.unit });

  const handleCommodityChange = (commodityIndex: number, updated: LtlCommodity) => {
    const commodities = [...unit.commodities];
    commodities[commodityIndex] = updated;
    onChange({ ...unit, commodities });
  };

  const handleAddCommodity = () =>
    onChange({ ...unit, commodities: [...unit.commodities, createEmptyCommodity(unit.commodities, displayWeightUnit)] });

  const handleRemoveCommodity = (commodityIndex: number) =>
    onChange({ ...unit, commodities: unit.commodities.filter((_, idx) => idx !== commodityIndex) });

  return (
    <Paper data-testid={`handling-unit-card-${index}`} p='md' shadow='none' withBorder>
      <Stack gap='sm'>
        <Group justify='space-between'>
          <Text fw={600}>{intl.formatMessage(handlingUnitsStepMessages.handlingUnitTitle, { number: index + 1 })}</Text>
          <Group gap='xs'>
            <CalculateDensityButton
              displayDimensionUnit={displayDimensionUnit}
              displayWeightUnit={displayWeightUnit}
              index={index}
              onChange={onChange}
              onDensityAction={onDensityAction}
              sellerFreightProviderId={sellerFreightProviderId}
              unit={unit}
            />
            {onRemove ? (
              <Button color='red' data-testid={`remove-unit-${index}`} onClick={onRemove} size='xs' variant='ghost'>
                {intl.formatMessage(handlingUnitsStepMessages.removeHandlingUnit)}
              </Button>
            ) : null}
          </Group>
        </Group>

        <Checkbox
          checked={unit.isStackable}
          data-testid={`unit-stackable-${index}`}
          label={intl.formatMessage(handlingUnitsStepMessages.stackableLabel)}
          onChange={(event) => handleStackableChange(event.currentTarget.checked)}
        />
        <Group grow>
          <CustomNumberInput
            allowNegative={false}
            data-testid={`unit-quantity-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.quantityLabel)}
            min={1}
            onChange={handleQuantityChange}
            value={unit.quantity}
          />
          <Dropdown
            data={packagingTypeData}
            data-testid={`unit-type-${index}`}
            label={intl.formatMessage(handlingUnitsStepMessages.unitTypeLabel)}
            onChange={handleTypeChange}
            value={unit.packagingType}
          />
        </Group>

        <Stack gap={4}>
          <Input.Label fw={500} required size='sm'>
            {intl.formatMessage(freightFormMessages.dimensionsLabel, { unit: dimensionsValue.unit })}
          </Input.Label>
          <DimensionsInput
            errors={
              fieldErrors && {
                length: fieldErrors['length'],
                width: fieldErrors['width'],
                height: fieldErrors['height'],
              }
            }
            onChange={handleDimensionsChange}
            value={dimensionsValue}
          />
        </Stack>

        {unit.commodities.map((commodity, cIdx) => (
          <WizardCommodityCard
            commodity={commodity}
            displayWeightUnit={displayWeightUnit}
            fieldErrors={fieldErrors ? sliceFieldErrors(fieldErrors, `commodities.${cIdx}`) : undefined}
            index={cIdx}
            isHazmatEnabled={isHazmatEnabled}
            key={commodity.id}
            onChange={(updated) => handleCommodityChange(cIdx, updated)}
            onCommodityPresetSelect={onCommodityPresetSelect}
            onRemove={cIdx > 0 ? () => handleRemoveCommodity(cIdx) : undefined}
          />
        ))}

        <Anchor component='button' data-testid={`add-commodity-${index}`} onClick={handleAddCommodity} size='sm' type='button'>
          {intl.formatMessage(handlingUnitsStepMessages.addCommodity)}
        </Anchor>
      </Stack>
    </Paper>
  );
};
